import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

const numberFormat = new Intl.NumberFormat('en-US');

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '0';
  return numberFormat.format(value);
}

export function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

/*
  Dates read the way the Tab Service portal writes them: month first, a twelve hour clock,
  AM or PM. Somebody who works in both products all day should not have to decide which one
  they are looking at before they can read a timestamp, and this is a US tax product whose
  customers write 9/18/2026 by hand.
*/

/** 9/18/2026, 3:06:26 PM. The stamp under a finished step, as the portal stamps its own. */
export function formatDateTime(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'M/d/yyyy, h:mm:ss a') : 'not yet';
}

/** 9/18/2026, 3:06 PM. For dense tables, where the seconds are noise. */
export function formatDateTimeShort(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'M/d/yyyy, h:mm a') : 'not yet';
}

/**
 * 3:06 PM today, 9/18/2026, 3:06 PM on any other day.
 *
 * For a time somebody is about to wait for. The clock alone is how a person says "in a few
 * minutes" to themselves, and the date only earns its place when the time is not today.
 */
export function formatClock(value: string | Date | null | undefined, now: Date = new Date()): string {
  const date = value instanceof Date ? value : toDate(value);
  if (!date) return 'not yet';
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  return sameDay ? format(date, 'h:mm a') : format(date, 'M/d/yyyy, h:mm a');
}

/** 9/18/2026 */
export function formatDate(value: string | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'M/d/yyyy') : 'not yet';
}

/** 4 minutes ago */
export function formatRelative(value: string | null | undefined): string {
  const date = toDate(value);
  if (!date) return 'not yet';
  if (Date.now() - date.getTime() < 60_000) return 'just now';
  return `${formatDistanceToNowStrict(date)} ago`;
}

/** Month key 2026-02 shown as February 2026 */
export function formatMonth(month: string): string {
  const parsed = parseISO(`${month}-01`);
  return isValid(parsed) ? format(parsed, 'MMMM yyyy') : month;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

/** Turns snake or kebab case into a sentence, for codes we have no copy for. */
export function humanize(value: string): string {
  const spaced = value.replace(/[_-]+/g, ' ').toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function initialsOf(email: string): string {
  const name = email.split('@')[0] ?? email;
  const parts = name.split(/[._-]+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}` : name.slice(0, 2);
  return letters.toUpperCase();
}
